/**
 * GNET Vesting Calculation
 *
 * Linear vesting over 24 months:
 * - Default Start: January 3, 2026 00:00:00 UTC
 * - Default End: January 3, 2028 00:00:00 UTC (24 months)
 *
 * Dates are configurable via Settings for testing on testnets.
 */

// Default vesting schedule (re-exported from hook for backwards compatibility)
export { DEFAULT_VESTING_START, DEFAULT_VESTING_END } from '@/shared/hooks/useContractSettings';

/**
 * Calculate how much has vested from the total amount based on current time
 */
export function calculateVestedAmount(
  totalAmount: bigint,
  vestingStart: number,
  vestingEnd: number,
  now: number = Date.now()
): bigint {
  if (now <= vestingStart) return 0n;
  if (now >= vestingEnd) return totalAmount;

  const elapsed = BigInt(now - vestingStart);
  const duration = BigInt(vestingEnd - vestingStart);

  return (totalAmount * elapsed) / duration;
}

/**
 * Calculate the amount available to sync (vested - already distributed)
 */
export function calculateToSync(
  totalAmount: bigint,
  distributedAmount: bigint,
  vestingStart: number,
  vestingEnd: number,
  now: number = Date.now()
): bigint {
  const vested = calculateVestedAmount(totalAmount, vestingStart, vestingEnd, now);
  const toSync = vested - distributedAmount;
  return toSync > 0n ? toSync : 0n;
}

/**
 * Get vesting progress as a percentage (0-100)
 */
export function getVestingProgress(
  vestingStart: number,
  vestingEnd: number,
  now: number = Date.now()
): number {
  if (now <= vestingStart) return 0;
  if (now >= vestingEnd) return 100;

  const elapsed = now - vestingStart;
  const duration = vestingEnd - vestingStart;
  return (elapsed / duration) * 100;
}

/**
 * Format a bigint wei amount to a human-readable string with decimals
 */
export function formatAmount(amount: bigint, decimals: number = 18): string {
  const divisor = 10n ** BigInt(decimals);
  const integerPart = amount / divisor;
  const fractionalPart = amount % divisor;

  if (fractionalPart === 0n) {
    return integerPart.toLocaleString('en-US');
  }

  const fractionalStr = fractionalPart.toString().padStart(decimals, '0').slice(0, 4);
  return `${integerPart.toLocaleString('en-US')}.${fractionalStr}`;
}

/**
 * Parse a decimal string to bigint wei
 */
export function parseAmount(amount: string, decimals: number = 18): bigint {
  const [integerPart, fractionalPart = ''] = amount.split('.');
  const paddedFractional = fractionalPart.padEnd(decimals, '0').slice(0, decimals);
  return BigInt(integerPart + paddedFractional);
}

/**
 * Get time remaining until full vesting
 */
export function getTimeRemaining(
  vestingStart: number,
  vestingEnd: number,
  now: number = Date.now()
): {
  days: number;
  hours: number;
  minutes: number;
  totalDays: number;
} {
  if (now >= vestingEnd) {
    return { days: 0, hours: 0, minutes: 0, totalDays: 0 };
  }

  const remaining = vestingEnd - Math.max(now, vestingStart);
  const totalDays = Math.ceil(remaining / (1000 * 60 * 60 * 24));
  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes, totalDays };
}
